import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
// A server function, not the Webshare API that `@/hooks/useSWR` builds keys for.
// eslint-disable-next-line no-restricted-imports
import useSWR from "swr";
import { dashboardBannersCms } from "@/content/dashboardBannerSource";
import type { DashboardBanner } from "@/content/dashboardBanners";
import { useAppPathname } from "@/routing/navigation";
import { PostHogEvents, useEvents } from "@/tracking/useEvents";
import {
  forgetClosedUnpublishedBanners,
  isClosed,
  rememberClose,
} from "./bannerCloses";
import {
  forgetUnpublishedCountdowns,
  isHiddenByCountdown,
} from "./bannerCountdowns";
import {
  type BannerPlace,
  bannersForPage,
  isOverlay,
  pickBanner,
} from "./bannerMatching";

/**
 * How often an open tab asks for the banners again, besides on focus: a
 * Result, an Off or an unpublish reaches a tab left open, which would
 * otherwise keep showing the old banner and recording its exposures. The
 * server holds the list, so asking costs no CMS read.
 */
const REFRESH_MS = 5 * 60_000;

/**
 * The published banner for this place to show on the current page, if any,
 * and how to follow a banner's CTA or close it, both reported to PostHog with
 * the banner's flag, variant and template. Both act on the banner they are
 * given, the one on screen, which a placement still animating out of a closed
 * banner holds after the runtime has picked the next. Banner flags are named in Sanity, so they are read here
 * by name rather than through `useIsFlagVariantTest`, which only takes
 * `ActiveVariantFeatureFlag`. A closed banner, and one whose countdown ran
 * out and hid it, is skipped before its flag is read, so it records no
 * exposure while hidden.
 */
export const useDashboardBanner = <P extends BannerPlace>(place: P) => {
  const pathname = useAppPathname();
  const { data: banners } = useSWR("dashboardBanners", loadBanners, {
    refreshInterval: REFRESH_MS,
  });
  const posthog = usePostHog();
  useRenderOnFlags(posthog);
  const [, setCloses] = useState(0);
  const navigate = useNavigate();
  const { captureEvent } = useEvents();

  const now = Date.now();
  const open = bannersForPage(banners ?? [], place, pathname).filter(
    (banner) => !isClosed(banner) && !isHiddenByCountdown(banner, now)
  );
  const banner = pickBanner(open, (flagKey) => {
    // An uninitialised client (server render, tests) throws; no flag, no banner.
    try {
      return posthog?.getFeatureFlag(flagKey);
    } catch {
      return undefined;
    }
  });

  const hide = (closed: DashboardBanner) => {
    rememberClose(closed);
    setCloses((closes) => closes + 1);
  };

  const followCta = (clicked: DashboardBanner) => {
    captureEvent(PostHogEvents.PromoBannerCtaClicked, eventOf(clicked));
    // A banner floating over the page is done with once its CTA is
    // followed: it would otherwise open again over the page the CTA led to.
    if (hidesOnClick(clicked)) hide(clicked);
    const { href } = clicked.cta;
    if (href.startsWith("/")) void navigate({ href });
    else window.location.assign(href);
  };

  const close = (closed: DashboardBanner) => {
    captureEvent(PostHogEvents.PromoBannerDismissed, eventOf(closed));
    hide(closed);
  };

  return { banner, followCta, close };
};

/**
 * Fetched once per load for every placement, so what is stored for
 * banners no longer published is forgotten once rather than per placement.
 */
const loadBanners = async () => {
  const banners = await dashboardBannersCms();
  forgetClosedUnpublishedBanners(banners);
  forgetUnpublishedCountdowns(banners);
  return banners;
};

/** A floating banner always; another when its editor asked and it can be closed. */
const hidesOnClick = (banner: DashboardBanner) =>
  isOverlay(banner) ||
  (banner.hideOnClick === true && banner.closePeriod !== null);

const eventOf = ({ experiment, template }: DashboardBanner) => ({
  flag: experiment.flagKey,
  variant: experiment.variant,
  template,
});

/** Flags arrive after the first render; render again when they do. */
const useRenderOnFlags = (posthog: ReturnType<typeof usePostHog>) => {
  const [, setFlagsLoads] = useState(0);
  useEffect(() => {
    if (!posthog?.__loaded) return;
    return posthog.onFeatureFlags(() => setFlagsLoads((loads) => loads + 1));
  }, [posthog]);
};
